import { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { Grid } from 'components/box';
import { Heading, Text } from 'components/typography';
import {
  MutationRequestPasswordResetArgs,
  MutationRequestCustomerPasswordResetOutput,
} from 'types/types';
import { closeDrawer, openDrawer } from '../../redux/global/actions';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import Button from 'components/button';
import FormError from 'components/form/error';
import LabeledInput from 'components/form/input';
import createDecorator from 'final-form-focus';
import { css } from '@emotion/react';
import isEmail from 'sane-email-validation';
import requestCustomerPasswordResetMutation from 'mutations/requestCustomerPasswordReset.graphql';
import { Box, Flex } from 'components/box';
import MailIcon from '../../shared/assets/icons/mail.svg';
import { useErrorNotificationHandler } from 'hooks/handle-errors';
import SectionTitle from 'components/_shared/elements/section-title';

const focusOnErrors = createDecorator();

const Header = ({ heading, message }: { heading: string; message: string }) => (
  <>
    <Heading fontSize="m" textAlign="left" mb={2} mt={2}>
      {heading}
    </Heading>
    <Heading fontSize="m" textAlign="left" mb={4}>
      {message}
    </Heading>
  </>
);

const RequestPasswordReset = ({
  showLoginBtn,
  loginBtnOnClick,
  showCloseBtn,
}: {
  showLoginBtn?: boolean;
  loginBtnOnClick?: () => void;
  showCloseBtn?: boolean;
}) => {
  const dispatch = useDispatch();
  const mountErrors = useErrorNotificationHandler();
  const [retry, setRetry] = useState(false);
  const [
    requestCustomerPasswordReset,
    { called, loading, error, data },
  ] = useMutation<
    MutationRequestCustomerPasswordResetOutput,
    MutationRequestPasswordResetArgs
  >(requestCustomerPasswordResetMutation);

  if (loading && retry) {
    // reset the retry state if we're trying again
    setRetry(false);
  }

  if (called && !loading && data?.requestCustomerPasswordReset) {
    return (
      <Box>
        <Flex
          mb="3"
          alignItems="center"
          justifyContent="center"
          css={theme => css`
            border-radius: 100%;
            width: 140px;
            height: 140px;
            margin: 0 auto;
            background-color: ${theme.colors.lightBlue};
          `}
        >
          <MailIcon
            css={theme => css`
              fill: ${theme.colors.blue};
              width: 85px;
              height: 85px;
            `}
          />
        </Flex>
        <Heading
          fontSize={['title5', 'title5']}
          textAlign="center"
          mt={4}
          mb={3}
        >
          Almost Done!
        </Heading>
        <Heading fontSize={[2]} textAlign="center" color="darkGrey">
          We have sent you further instructions. Please check your inbox.
        </Heading>
      </Box>
    );
  }

  if (error && !retry) {
    const [firstError] = error.graphQLErrors;
    return (
      <>
        <Header
          heading="Whoops!"
          message={
            firstError.message ||
            'Something went wrong, please try again later.'
          }
        />
        <div
          css={css`
            text-align: center;
            margin-top: 1em;
          `}
        >
          <Button width="100%" mb={3} onClick={() => setRetry(true)}>
            Try Again
          </Button>
        </div>
      </>
    );
  }

  const close = () => dispatch(closeDrawer());

  return (
    <Form
      decorators={[focusOnErrors]}
      onSubmit={({ email }) => {
        if (typeof window !== 'undefined') {
          requestCustomerPasswordReset({
            variables: {
              email,
              location: `${window.location.protocol}//${window.location.host}/`,
            },
          })
            .then()
            .catch(e => mountErrors([...e.graphQLErrors]));
        }
      }}
    >
      {({ handleSubmit, submitError }) => (
        <form onSubmit={handleSubmit} noValidate>
          <SectionTitle
            fontSize={3}
            title="Forgot your password?"
            closeButtonCallback={showCloseBtn ? close : undefined}
          />

          <Grid mt={3}>
            <Text lineHeight="1.4">
              Add your email address below to reset your password. We will send
              you an email with a link for you to choose a new password.
            </Text>
            <Field
              name="email"
              validate={value => {
                if (!value) {
                  return 'Enter your email';
                }
                if (!isEmail(value)) {
                  return 'Enter a valid email';
                }
              }}
            >
              {({ input, meta }) => (
                <LabeledInput
                  mb={8}
                  label="Email Address"
                  {...input}
                  type="email"
                  placeholder="Email"
                  invalid={meta.error && meta.touched}
                  error={meta.touched && meta.error}
                />
              )}
            </Field>

            {submitError && <FormError>{submitError}</FormError>}

            <Button
              width="100%"
              mb={3}
              disabled={loading}
              loading={loading}
              type="submit"
            >
              Send reset email
            </Button>

            {showLoginBtn && (
              <Text fontSize={2} color="darkGrey" textAlign="center">
                Remembered your password?{' '}
                <Button
                  p="0"
                  variant="flat"
                  display="inline-block"
                  textTransform="none"
                  lineHeight={0}
                  onClick={() =>
                    loginBtnOnClick
                      ? loginBtnOnClick()
                      : dispatch(openDrawer('login'))
                  }
                  css={theme => css`
                    letter-spacing: 0px;
                    font-family: ${theme.fonts.body};
                  `}
                >
                  Log in
                </Button>
              </Text>
            )}
          </Grid>
        </form>
      )}
    </Form>
  );
};

export default RequestPasswordReset;
